import React, { useRef, useEffect, useState } from 'react';
import VideoPlayer from './components/VideoPlayer';
import TranscriptBlocks from './components/TranscriptBlocks';
import Toolbar from './components/Toolbar';
import Sidebar from './components/Sidebar';
import { CloudArrowUpIcon, ArrowDownTrayIcon, ClockIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { getRelativeTime } from './utils/timeFormatter';
import ReplaceAllModal from './components/Modals/ReplaceAllModal';
import AssignToSpeakerModal from './components/Modals/AssignToSpeakerModal';
import useTranscriptState from './hooks/useTranscriptState';
import useVideoSync from './hooks/useVideoSync';
import useAutoSave from './hooks/useAutoSave';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import useTextSelection from './hooks/useTextSelection';
import useLockManagement from './hooks/useLockManagement';
import usePlaybackControl from './hooks/usePlaybackControl';
import { countOccurrences } from './utils/transcriptOperations';
import { CONFIG } from './constants/config';

/**
 * TranscriptEditor Component
 * 
 * Main transcript editing interface with video sync
 */
const TranscriptEditor = ({
  sessionId,
  videoUrl,
  initialBlocks = [],
  transcriptLoading = false,
  onExport,
  sessionTitle,
  backUrl,
  className = '',
}) => {
  const videoRef = useRef(null);
  const videoPlayerFullscreenRef = useRef(null);
  const transcriptScrollParentRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Replace All Modal state
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceSearchText, setReplaceSearchText] = useState('');
  // Assign to Speaker Modal state
  const [showAssignSpeakerModal, setShowAssignSpeakerModal] = useState(false);

  /**
   * Lock management
   */
  const {
    lockStatus,
    hasLock,
    lockLoading,
    acquireLock: handleAcquireLock,
    releaseLock: handleReleaseLock,
    forceOverrideLock: handleForceOverride,
  } = useLockManagement(sessionId);

  /**
   * Transcript state management
   */
  const {
    blocks,
    selectedBlocks,
    focusedBlock,
    speakers,
    searchQuery,
    searchResults,
    currentSearchIndex,
    undo,
    redo,
    canUndo,
    canRedo,
    handleSplit,
    handleMergeUp,
    handleMergeDown,
    handleInsertBlock,
    handleDelete,
    handleDuplicate,
    handleUpdateText,
    handleUpdateSpeaker,
    handleBulkUpdateSpeaker,
    handleReorder,
    handleMoveUp,
    handleMoveDown,
    handleFocusBlock,
    handleBlurBlock,
    handleFocusNext,
    handleFocusPrevious,
    handleSelectBlock,
    handleSelectRange,
    handleClearSelection,
    handleSearch,
    handleNextSearch,
    handlePreviousSearch,
    handleReplace,
    handleAddSpeaker,
    handleRenameSpeaker,
    handleDeleteSpeaker,
    handleReplaceAll,
    handleAssignSelectionToSpeaker,
  } = useTranscriptState(initialBlocks, sessionId);

  /**
   * Video sync
   */
  const {
    currentBlockId,
    currentTime,
    duration,
    isPlaying,
    seekTo,
    seekToBlock,
    togglePlay,
    play,
    pause,
    skip,
    setPlaybackRate,
  } = useVideoSync(blocks, videoRef, {
    autoScroll: false,
    scrollBehavior: 'smooth',
    scrollBlock: 'center',
  });

  /**
   * Auto-save
   */
  const {
    isSaving,
    lastSaved,
    saveError,
    isDirty,
    manualSave,
    manualSaveVersion,
  } = useAutoSave(blocks, sessionId, CONFIG.AUTO_SAVE_DELAY, true);

  /**
   * Text selection for Replace All
   */
  const { selection, clearSelection } = useTextSelection(transcriptContainerRef);

  /**
   * Playback control
   */
  const { playFromSelection } = usePlaybackControl(blocks, seekToBlock, seekTo, play);

  /**
   * Keyboard shortcuts
   */
  useKeyboardShortcuts(
    {
      onSplit: () => {
        if (focusedBlock) {
          // Cursor position will be handled by TranscriptBlock component
        }
      },
      onMergeUp: () => {
        if (focusedBlock) {
          handleMergeUp(focusedBlock);
        }
      },
      onMergeDown: () => {
        if (focusedBlock) {
          handleMergeDown(focusedBlock);
        }
      },
      onDelete: () => {
        if (focusedBlock) {
          handleDelete(focusedBlock);
        }
      },
      onDuplicate: () => {
        if (focusedBlock) {
          handleDuplicate(focusedBlock);
        }
      },
      onUndo: undo,
      onRedo: redo,
      onSave: manualSave,
      onSearch: () => {
        // Will be handled by Toolbar SearchBar
      },
      onPlayPause: togglePlay,
      onSkipForward: (seconds) => skip(seconds),
      onSkipBackward: (seconds) => skip(seconds),
      onNextBlock: handleFocusNext,
      onPreviousBlock: handleFocusPrevious,
      onMoveUp: () => {
        if (focusedBlock) {
          handleMoveUp(focusedBlock);
        }
      },
      onMoveDown: () => {
        if (focusedBlock) {
          handleMoveDown(focusedBlock);
        }
      },
      onFullscreen: () => {
        if (videoPlayerFullscreenRef.current) {
          videoPlayerFullscreenRef.current();
        }
      },
    },
    {
      enabled: true,
      skipForwardTime: CONFIG.SKIP_FORWARD_TIME,
      skipBackwardTime: CONFIG.SKIP_BACKWARD_TIME,
      fineTuneTime: CONFIG.FINE_TUNE_TIME,
    }
  );

  /**
   * Handle export
   */
  const handleExport = (format) => {
    onExport?.(format, blocks, sessionTitle);
  };

  /**
   * Handle speaker selection from sidebar
   */
  const handleSpeakerSelection = (speakerName) => {
    if (selectedBlocks.length > 0) {
      handleBulkUpdateSpeaker(selectedBlocks, speakerName);
    }
  };

  /**
   * Handle Replace All - Open modal
   */
  const handleOpenReplaceModal = (selectedText) => {
    setReplaceSearchText(selectedText);
    setShowReplaceModal(true);
  };

  /**
   * Handle Replace All - Execute replacement
   */
  const handleExecuteReplaceAll = (searchText, replaceText) => {
    const result = handleReplaceAll(searchText, replaceText);
    
    if (result.success) {
      clearSelection();
      setShowReplaceModal(false);
    }
    
    return result;
  };

  /**
   * Open Assign to Speaker modal (user has selected text and clicked Assign to speaker)
   */
  const handleOpenAssignSpeakerModal = () => {
    setShowAssignSpeakerModal(true);
  };

  /**
   * Assign selection to chosen speaker and close modal
   */
  const handleAssignSelectionToSpeakerConfirm = (speakerName) => {
    const result = handleAssignSelectionToSpeaker(selection, speakerName);
    if (result.success) {
      clearSelection();
      setShowAssignSpeakerModal(false);
    } else if (result.error) {
      alert(result.error);
    }
  };

  /**
   * Initialize component
   */
  useEffect(() => {
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-screen bg-gray-50 overflow-hidden ${className}`}>
      {/* Toolbar - fixed at top */}
      <Toolbar
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onSearch={handleSearch}
        onReplace={handleReplace}
        onNextSearch={handleNextSearch}
        onPreviousSearch={handlePreviousSearch}
        searchResults={searchResults}
        currentSearchIndex={currentSearchIndex}
        lockStatus={lockStatus}
        hasLock={hasLock}
        onAcquireLock={handleAcquireLock}
        onReleaseLock={handleReleaseLock}
        onForceOverride={handleForceOverride}
        lockLoading={lockLoading}
        sessionTitle={sessionTitle}
        backUrl={backUrl}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Overlay kada user nema lock — celo telo stranice je posivljeno */}
        {!hasLock && (
          <div
            className={`absolute inset-0 bg-gray-500/20 backdrop-blur-[1px] z-30 transition-opacity duration-300 ${
              lockStatus?.locked ? 'pointer-events-auto' : 'pointer-events-none'
            }`}
            aria-hidden="true"
          />
        )}

        {/* Left Sidebar - Options */}
        <div className="relative w-64 flex-shrink-0">
          {/* Blur Overlay kada je zaključano */}
          {!hasLock && lockStatus.locked && (
            <div className="absolute inset-0 bg-gray-50/90 backdrop-blur-sm z-40 flex items-center justify-center pointer-events-auto transition-all duration-300 ease-in-out">
              <div className="text-center p-6 bg-white rounded-xl shadow-lg border-2 border-red-200 max-w-[90%] animate-fade-in">
                <svg className="h-12 w-12 text-red-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-sm font-semibold text-gray-900 mb-1">Options Locked</p>
                <p className="text-xs text-gray-600">
                  Locked by <strong className="text-red-600">{lockStatus.locked_by_name}</strong>
                </p>
              </div>
            </div>
          )}
          <Sidebar
            sessionId={sessionId}
            speakers={speakers}
            onSelectSpeaker={handleSpeakerSelection}
            onAddSpeaker={handleAddSpeaker}
            onRenameSpeaker={handleRenameSpeaker}
            onDeleteSpeaker={handleDeleteSpeaker}
            onExport={handleExport}
            onReplaceAll={handleOpenReplaceModal}
            onPlayFromSelection={playFromSelection}
            onAssignSelectionToSpeaker={handleOpenAssignSpeakerModal}
            selectedText={selection?.text}
            selection={selection}
            className={`overflow-y-auto h-full ${!hasLock && lockStatus.locked ? 'pointer-events-none' : ''}`}
          />
        </div>

        {/* Central Area - Transcript Blocks */}
        <div 
          className="flex-1 overflow-y-auto p-6 pb-24 bg-white relative"
          style={{ scrollMargin: 0, scrollPadding: 0 }}
          ref={transcriptScrollParentRef}
        >
          <div 
            className="max-w-4xl mx-auto relative" 
            ref={transcriptContainerRef}
            style={{ scrollMargin: 0, scrollPadding: 0 }}
          >
            <TranscriptBlocks
              blocks={blocks}
              speakers={speakers}
              transcriptLoading={transcriptLoading}
              currentBlockId={currentBlockId}
              focusedBlock={focusedBlock}
              selectedBlocks={selectedBlocks}
              onTextChange={hasLock ? handleUpdateText : () => {}}
              onSpeakerChange={hasLock ? handleUpdateSpeaker : () => {}}
              onFocusBlock={handleFocusBlock}
              onBlurBlock={handleBlurBlock}
              onSelectBlock={handleSelectBlock}
              onSplit={hasLock ? handleSplit : () => {}}
              onMergeUp={hasLock ? handleMergeUp : () => {}}
              onMergeDown={hasLock ? handleMergeDown : () => {}}
              onInsertBlock={hasLock ? handleInsertBlock : () => {}}
              onDelete={hasLock ? handleDelete : () => {}}
              onDuplicate={hasLock ? handleDuplicate : () => {}}
              onSeekToBlock={seekToBlock}
              onSeekTo={seekTo}
              onPlay={play}
              currentTime={currentTime}
              isPlaying={isPlaying}
              readOnly={!hasLock}
              scrollParentRef={transcriptScrollParentRef}
            />
          </div>
        </div>

        {/* Right Sidebar - Video Player */}
        <div className="w-96 flex-shrink-0 bg-gradient-to-b from-gray-50 to-gray-100 border-l border-gray-200 overflow-y-auto">
          <div className="sticky top-0 p-4 space-y-3">
            {/* Video Title */}
            <div className="flex items-center gap-2 px-1">
              <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-700">Video Playback</h3>
            </div>
            
            {/* Video Container */}
            <div className="bg-white rounded-xl shadow-lg p-3 border border-gray-200">
              <VideoPlayer
                ref={videoRef}
                src={videoUrl}
                isPlaying={isPlaying}
                className="w-full rounded-lg overflow-hidden"
                onFullscreenToggle={videoPlayerFullscreenRef}
              />
            </div>

            {/* Save & Export - always visible below video */}
            <div className="bg-white rounded-xl shadow-lg p-3 border border-gray-200 space-y-3">
              {/* Save Status */}
              <div className="min-h-[24px]">
                {isSaving && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 animate-spin" />
                    Saving...
                  </div>
                )}
                {!isSaving && saveError && (
                  <div className="flex items-center gap-2 text-sm text-red-600" title={saveError}>
                    <ExclamationCircleIcon className="h-4 w-4" />
                    Save failed
                  </div>
                )}
                {!isSaving && !saveError && isDirty && (
                  <div className="flex items-center gap-2 text-sm text-orange-600">
                    <ClockIcon className="h-4 w-4" />
                    Unsaved changes
                  </div>
                )}
                {!isSaving && !saveError && !isDirty && lastSaved && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircleIcon className="h-4 w-4" />
                    Saved {getRelativeTime(lastSaved)}
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={manualSave}
                  disabled={isSaving || !isDirty || !hasLock}
                  className={`
                    flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors
                    ${
                      isDirty && !isSaving && hasLock
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }
                  `}
                  title={!hasLock ? "You need to acquire the lock to save" : "Save (Cmd+S)"}
                >
                  <CloudArrowUpIcon className="h-5 w-5" />
                  Save
                </button>
                <button
                  onClick={() => handleExport('pdf')}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                  title="Export transcript"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Replace All Modal */}
      <ReplaceAllModal
        isOpen={showReplaceModal}
        onClose={() => {
          setShowReplaceModal(false);
          clearSelection();
        }}
        onReplace={handleExecuteReplaceAll}
        searchText={replaceSearchText}
        occurrenceCount={countOccurrences(blocks, replaceSearchText)}
      />

      {/* Assign to Speaker Modal */}
      <AssignToSpeakerModal
        isOpen={showAssignSpeakerModal}
        onClose={() => {
          setShowAssignSpeakerModal(false);
        }}
        speakers={speakers}
        selectedTextPreview={selection?.text}
        onSelectSpeaker={handleAssignSelectionToSpeakerConfirm}
      />
    </div>
  );
};

export default TranscriptEditor;

